import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import db from "./services/DatabaseManager.js";

import AlertModal from "./modals/AlertModal.jsx";
import ConfirmModal from "./modals/ConfirmModal.jsx";
import RoomsModal from "./modals/RoomsModal.jsx";
import AdminModal from "./modals/AdminModal.jsx";
import CsvModal from "./modals/CsvModal.jsx";
import AvatarCropModal from "./modals/AvatarCropModal.jsx";
import DemoRoomModal from "./modals/DemoRoomModal.jsx";
import ColorPicker from "./ColorPicker.jsx";
import AvatarPicker from "./AvatarPicker.jsx";

import cssText from "./styles/landing.js";
import acknowledgementImage from "../info/Acknowledgement-Image.png";
import { useModals } from "./hooks/useModals.js";
import { useAdmin } from "./hooks/useAdmin.js";
import { useRooms } from "./hooks/useRooms.js";
import { useAuth } from "./hooks/useAuth.js";
import { useCsvImport } from "./hooks/useCsvImport.js";

const DEMO_CODE_ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";

const generateDemoCode = () => {
    const bytes = new Uint8Array(6);
    crypto.getRandomValues(bytes);

    return Array.from(
        bytes,
        (byte) => DEMO_CODE_ALPHABET[byte % DEMO_CODE_ALPHABET.length],
    ).join("");
};

const LandingPage = () => {
    const [roomCode, setRoomCode] = useState("");
    const [loginOpen, setLoginOpen] = useState(false);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isTeacherMode, setIsTeacherMode] = useState(false);

    const {
        alertModal,
        confirmModal,
        showAlert,
        closeAlert,
        showConfirm,
        closeConfirm,
        handleConfirmAction,
    } = useModals();

    const csv = useCsvImport(db, { showAlert, showConfirm });
    const [csvModalType, setCsvModalType] = useState(null);
    const [csvModalExtraParams, setCsvModalExtraParams] = useState(null);

    const {
        teacherUser,
        teacherName,
        studentName,
        studentColor,
        studentAvatar,
        currentAvatar,
        savingAvatar,
        adminUser,
        loginError,
        setLoginError,
        currentUser,
        isLoggedIn,
        handleLogin,
        handleLogout,
        setStudentName,
        setStudentColor,
        setStudentAvatar,
        saveTeacherAvatar,
    } = useAuth(db);
    const {
        teachers,
        isAddingTeacher,
        deletingTeacherUid,
        retrieveTeachers,
        handleAddTeacher,
        handleDeleteTeacher,
        onMakeAdmin,
        makingAdminUid,
        editingTeacher,
        setEditingTeacher,
        savingTeacherUid,
        handleUpdateTeacher,
    } = useAdmin(db, { showAlert, showConfirm });
    const {
        rooms,
        retrieveRooms,
        handleCreateRoom,
        handleDeleteRoom,
        handleToggleLock,
        handleAddStudent,
        handleRemoveStudent,
        handleAddTag,
        handleRemoveTag,
        handleMakeTagPrimary,
        handleUpdateRoomNote,
        handleLockRoom,
    } = useRooms(db, { showAlert, showConfirm });

    const [adminModalOpen, setAdminModalOpen] = useState(false);
    const [roomsModalOpen, setRoomsModalOpen] = useState(false);
    const [csvModalOpen, setCsvModalOpen] = useState(false);

    const [cropFile, setCropFile] = useState(null);
    const [demoCode, setDemoCode] = useState(null);
    const [creatingDemo, setCreatingDemo] = useState(false);

    const dropdownRef = useRef(null);

    useEffect(() => {
        const parmams = new URLSearchParams(window.location.search);
        if (parmams.toString() === "") return;

        window.location.replace(window.location.origin);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !event.target.closest(
                    ".color-picker-popover, .avatar-picker-popover",
                )
            ) {
                setLoginOpen(false);
                setIsTeacherMode(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        const result = await handleLogin(username, password, isTeacherMode);
        if (result.success) {
            setLoginOpen(false);
            setUsername("");
            setPassword("");
            if (!isTeacherMode) setIsTeacherMode(false);
        }
    };

    const handleEnter = async () => {
        const code = roomCode.trim();
        const roomSnap = await db.getRoom(code);
        let validUser = false;
        let msg = "",
            title = "";

        if (roomSnap === null) {
            title = "¡Sala no encontrada!";
            msg = "La sala a la que intentas entrar no existe.";
            validUser = false;
        } else {
            title = "¡Permisos insuficientes!";
            msg = "No tienes permiso para entrar en esta sala.";
            if (teacherUser && teacherUser.uid === roomSnap.teacher)
                validUser = true;
            if (studentName && roomSnap.allowedStudents.includes(studentName))
                validUser = true;
            if (!roomSnap.isPrivate) validUser = true;
        }

        if (!validUser) {
            showAlert(title, msg);
            return;
        }

        if (code && isLoggedIn) enterRoom(code);
    };

    const enterRoom = (code) => {
        if (teacherUser) {
            window.location.href = `app.html?room=${encodeURIComponent(code)}`;
            return;
        }

        window.location.href =
            `app.html?room=${encodeURIComponent(code)}` +
            `&user=${encodeURIComponent(studentName)}` +
            `&color=${encodeURIComponent(studentColor)}` +
            `&avatar=${encodeURIComponent(studentAvatar)}`;
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleEnter();
    };

    const handleAvatarChange = async (avatar) => {
        if (!teacherUser) {
            setStudentAvatar(avatar);
            return;
        }

        const result = await saveTeacherAvatar(avatar);
        if (!result.success)
            showAlert("No se pudo guardar el avatar", result.error);
    };

    const handleCropSave = async (dataUrl) => {
        await handleAvatarChange(dataUrl);
        setCropFile(null);
    };

    const handleCreateDemoRoom = async () => {
        if (creatingDemo) return;
        setCreatingDemo(true);

        try {
            await db.createDemoRoom(demoCode);
            enterRoom(demoCode);
        } catch (error) {
            setCreatingDemo(false);
            showAlert("No se pudo crear la sala", error.message);
        }
    };

    const handleReplayRoom = (room) => {
        window.location.href = `app.html?replay=${encodeURIComponent(room.id)}`;
    };

    // CSV
    const openCsvModal = (type, extraParams = null) => {
        setCsvModalType(type);
        setCsvModalExtraParams(extraParams);
        csv.reset(type);
        setCsvModalOpen(true);
    };

    const closeCsvModal = (type, extraParams = null) => {
        setCsvModalOpen(false);
        setCsvModalType(null);
        setCsvModalExtraParams(null);
    };

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: cssText }} />
            <div className="landing-wrapper">
                <header className="landing-header">
                    <a
                        href="https://scratch.mit.edu"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="scratch-logo-link"
                        title="Ir a Scratch oficial"
                    >
                        <img
                            src="https://scratch.mit.edu/images/logo_sm.png"
                            alt="Scratch"
                            className="scratch-logo"
                        />
                    </a>

                    <div className="login-dropdown" ref={dropdownRef}>
                        {isLoggedIn ? (
                            <div className="user-badge">
                                {!teacherUser && (
                                    <button
                                        className="demo-room-btn"
                                        title="Crear una sala de prueba temporal"
                                        onClick={() =>
                                            setDemoCode(generateDemoCode())
                                        }
                                    >
                                        Demo
                                    </button>
                                )}
                                <AvatarPicker
                                    value={currentAvatar}
                                    color={
                                        teacherUser ? "#ffd166" : studentColor
                                    }
                                    onChange={handleAvatarChange}
                                    allowPhoto={!!teacherUser}
                                    onRequestPhoto={setCropFile}
                                />
                                {!teacherUser && (
                                    <ColorPicker
                                        color={studentColor}
                                        onChange={setStudentColor}
                                        label="Tu color"
                                    />
                                )}
                                <span>{currentUser}</span>
                                {teacherUser && (
                                    <button
                                        className="rooms-btn"
                                        onClick={async () => {
                                            await retrieveRooms();
                                            setRoomsModalOpen(true);
                                        }}
                                    >
                                        Mis salas
                                    </button>
                                )}
                                <button
                                    className="logout-btn"
                                    onClick={handleLogout}
                                    title="Cerrar sesión"
                                >
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <>
                                <button
                                    className="login-toggle"
                                    onClick={() => setLoginOpen(!loginOpen)}
                                >
                                    <span className="login-text">
                                        Iniciar sesión
                                    </span>
                                    <span
                                        className={`arrow ${loginOpen ? "open" : ""}`}
                                    >
                                        ▼
                                    </span>
                                </button>

                                {loginOpen && (
                                    <div className="login-panel">
                                        <form onSubmit={onSubmit}>
                                            <h3>
                                                {isTeacherMode
                                                    ? "Iniciar Sesión"
                                                    : "Introduce tu nombre"}
                                            </h3>

                                            <div className="form-group">
                                                <label>
                                                    {isTeacherMode
                                                        ? "Correo electrónico"
                                                        : "Nombre"}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={username}
                                                    onChange={(e) =>
                                                        setUsername(
                                                            e.target.value,
                                                        )
                                                    }
                                                    required
                                                />
                                            </div>

                                            {!isTeacherMode && (
                                                <div className="identity-row">
                                                    <div className="identity-item">
                                                        <label>Avatar</label>
                                                        <AvatarPicker
                                                            value={studentAvatar}
                                                            color={studentColor}
                                                            onChange={
                                                                setStudentAvatar
                                                            }
                                                        />
                                                    </div>
                                                    <div className="identity-item">
                                                        <label>Color</label>
                                                        <ColorPicker
                                                            color={studentColor}
                                                            onChange={
                                                                setStudentColor
                                                            }
                                                            label="Tu color"
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {isTeacherMode && (
                                                <div
                                                    className="form-group"
                                                    style={{
                                                        animation:
                                                            "slideDown 0.2s ease-out",
                                                    }}
                                                >
                                                    <label>Contraseña</label>
                                                    <input
                                                        type="password"
                                                        value={password}
                                                        onChange={(e) =>
                                                            setPassword(
                                                                e.target.value,
                                                            )
                                                        }
                                                        required
                                                    />
                                                </div>
                                            )}

                                            {isTeacherMode && loginError && (
                                                <p className="error-message">
                                                    {loginError}
                                                </p>
                                            )}

                                            <button
                                                type="submit"
                                                className="login-submit"
                                            >
                                                Entrar
                                            </button>

                                            <div className="login-links">
                                                {!isTeacherMode ? (
                                                    <button
                                                        type="button"
                                                        className="link-button"
                                                        onClick={() => {
                                                            setIsTeacherMode(
                                                                true,
                                                            );
                                                            setLoginError("");
                                                        }}
                                                    >
                                                        Entrar como profesor
                                                    </button>
                                                ) : (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="link-button"
                                                            onClick={() =>
                                                                alert(
                                                                    "TODO: Redirigir a recuperación de contraseña",
                                                                )
                                                            }
                                                        >
                                                            Olvidé mi contraseña
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="link-button"
                                                            onClick={() => {
                                                                setIsTeacherMode(
                                                                    false,
                                                                );
                                                                setPassword("");
                                                            }}
                                                        >
                                                            Entrar como alumno
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </form>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </header>

                <main className="landing-main">
                    <div className="coscratch-brand">
                        <div className="coscratch-logo-placeholder">
                            <span className="logo-text">CoScratch</span>
                        </div>
                    </div>

                    <div className="room-section">
                        <label htmlFor="room-code" className="room-label">
                            Código de sala
                        </label>
                        <input
                            id="room-code"
                            type="text"
                            className="room-input"
                            value={roomCode}
                            onChange={(e) => setRoomCode(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Ej: 2frd"
                            maxLength={20}
                        />
                        <button
                            className="enter-room-btn"
                            onClick={handleEnter}
                            disabled={!roomCode.trim() || !isLoggedIn}
                        >
                            {!isLoggedIn
                                ? "Inicia sesión para entrar a una sala"
                                : !roomCode.trim()
                                  ? "Introduce el código de la sala"
                                  : "Entrar a la sala"}
                        </button>
                    </div>
                </main>

                <footer className="landing-footer">
                    <p>Powered by Scratch • Universidad Rey Juan Carlos</p>
                    <img
                        className="landing-acknowledgement"
                        src={acknowledgementImage}
                        alt="Proyecto de I+D+i PID2022-137849OB-I00, financiado por el Ministerio de Ciencia, Innovación y Universidades, la Unión Europea y la Agencia Estatal de Investigación"
                    />
                </footer>
                {adminUser && (
                    <button
                        className="admin-corner-btn"
                        onClick={async () => {
                            await retrieveTeachers();
                            setAdminModalOpen(true);
                        }}
                    >
                        Panel de administración
                    </button>
                )}
            </div>

            <DemoRoomModal
                open={!!demoCode}
                code={demoCode}
                creating={creatingDemo}
                onCreate={handleCreateDemoRoom}
                onClose={() => setDemoCode(null)}
            />

            <AvatarCropModal
                open={!!cropFile}
                file={cropFile}
                saving={savingAvatar}
                onCancel={() => setCropFile(null)}
                onSave={handleCropSave}
            />

            <AlertModal {...alertModal} onClose={closeAlert} />
            <ConfirmModal
                {...confirmModal}
                onConfirm={handleConfirmAction}
                onClose={closeConfirm}
            />

            {adminModalOpen && (
                <AdminModal
                    db={db}
                    open={adminModalOpen}
                    currentEmail={teacherUser.email}
                    onClose={() => setAdminModalOpen(false)}
                    teachers={teachers}
                    isAddingTeacher={isAddingTeacher}
                    deletingTeacherUid={deletingTeacherUid}
                    onAddTeacher={handleAddTeacher}
                    onDeleteTeacher={handleDeleteTeacher}
                    onMakeAdmin={onMakeAdmin}
                    makingAdminUid={makingAdminUid}
                    editingTeacherUid={editingTeacher}
                    setEditingTeacherUid={setEditingTeacher}
                    savingTeacherUid={savingTeacherUid}
                    onUpdateTeacher={handleUpdateTeacher}
                    onImportCSV={() => openCsvModal("teachers")}
                />
            )}

            {roomsModalOpen && (
                <RoomsModal
                    open={roomsModalOpen}
                    onClose={() => setRoomsModalOpen(false)}
                    rooms={rooms}
                    onCreateRoom={handleCreateRoom}
                    onDeleteRoom={handleDeleteRoom}
                    onToggleLock={handleToggleLock}
                    onAddStudent={handleAddStudent}
                    onRemoveStudent={handleRemoveStudent}
                    onAddTag={handleAddTag}
                    onRemoveTag={handleRemoveTag}
                    onMakeTagPrimary={handleMakeTagPrimary}
                    roomsCsv={() => openCsvModal("rooms")}
                    studentsCsv={openCsvModal}
                    onUpdateRoomNote={handleUpdateRoomNote}
                    onToggleRoomLock={handleLockRoom}
                    onReplayRoom={handleReplayRoom}
                />
            )}

            {csvModalOpen && csvModalType && (
                <CsvModal
                    type={csvModalType}
                    open={csvModalOpen}
                    onClose={closeCsvModal}
                    file={csv[csvModalType].file}
                    preview={csv[csvModalType].preview}
                    isImporting={csv[csvModalType].importing}
                    results={csv[csvModalType].results}
                    onFileChange={csv.handleFileChange(csvModalType)}
                    onImport={async () => {
                        const result = await csv.importCsv(
                            csvModalType,
                            csvModalExtraParams,
                        );
                        if (csvModalType === "students" && result)
                            await retrieveRooms();
                        if (csvModalType === "rooms" && result)
                            await retrieveRooms();
                        if (csvModalType === "teachers" && result)
                            await retrieveTeachers();
                        return result;
                    }}
                    onReset={csv.reset}
                    extraParam={csvModalExtraParams}
                />
            )}
        </>
    );
};

ReactDOM.render(<LandingPage />, document.getElementById("root"));
